/**
 * `<caos-limits-panel>` — a grouped board of allowances, each drawn as a usage bar.
 * (Catalog: Status & feedback › LimitsPanel — a grouped board of allowances, each drawn as a usage bar, summarising the ones under pressure.)
 *
 * Org Status is one screen holding every limit an organisation has, grouped hourly,
 * daily, monthly, event usage, installed packages and settings. Read end to end it is
 * forty numbers; read the way people actually read it, it is one question — is anything
 * about to run out — and the panel exists to answer that question before the reader has
 * to scan for it.
 *
 * So the summary line is the component, and the bars are its evidence. It counts the
 * limits over each threshold and says so at the top, using {@link usageIntent} — the
 * SAME function the individual bars use. That sharing is the point rather than a tidy-up:
 * a panel that computed "3 near their limit" by its own rule would eventually disagree
 * with the bars beneath it, and a summary contradicting its own evidence is worse than no
 * summary at all.
 *
 * Groups are ordered as given, never sorted. The groups are the platform's own
 * vocabulary — hourly before daily before monthly — and an alphabetical re-ordering
 * would put "daily" before "hourly" and destroy the only ordering a reader brings with
 * them.
 *
 * It renders its own `<caos-usage-bar>` children from `limits`, rather than taking them
 * as slotted markup. The summary has to be able to count what it is summarising, and a
 * panel that counted slotted children would be reporting on markup it does not control —
 * the case where the number and the bars drift apart. Styling seams: `::part(panel)`,
 * `::part(summary)`, `::part(group)`, `::part(group-name)`, `::part(limit)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { usageIntent, USAGE_WARN_AT, USAGE_DANGER_AT, type CaosUsageIntent } from './usage-bar.js';
import './usage-bar.js';

/** One allowance the panel draws. */
export interface CaosLimit {
  readonly label: string;
  readonly used: number;
  readonly maximum: number;
  /** Which group it belongs under. Absent puts it in the panel's ungrouped lead section. */
  readonly group?: string;
  readonly unit?: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  .panel { display: flex; flex-direction: column; gap: var(--caos-space-5); }

  .summary {
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
  .summary[data-pressure="warning"] strong { color: var(--caos-color-warning); }
  .summary[data-pressure="danger"] strong { color: var(--caos-color-danger); }

  .group { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .group-name {
    font-family: var(--caos-font);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }

  .limits { display: flex; flex-direction: column; gap: var(--caos-space-4); }

  .empty {
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
`);

export class CaosLimitsPanel extends CaosElement {
  static get observedAttributes(): string[] {
    return ['warn-at', 'danger-at', 'empty-text'];
  }

  private panelEl: HTMLDivElement;
  private summaryEl: HTMLParagraphElement;
  private bodyEl: HTMLDivElement;
  private data: readonly CaosLimit[] = [];

  constructor() {
    super(sheet);

    this.panelEl = document.createElement('div');
    this.panelEl.className = 'panel';
    this.panelEl.setAttribute('part', 'panel');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    // The summary changes as limits are refreshed; a reader who is not looking at it should
    // still be told the org has moved into trouble.
    this.summaryEl.setAttribute('role', 'status');

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'panel';

    this.panelEl.append(this.summaryEl, this.bodyEl);
    this.root.append(this.panelEl);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** The allowances to draw. Setting it re-renders; the panel owns its own bars. */
  get limits(): readonly CaosLimit[] {
    return this.data;
  }

  set limits(next: readonly CaosLimit[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  private get warnAt(): number {
    const n = Number(this.getAttribute('warn-at'));
    return Number.isFinite(n) && this.hasAttribute('warn-at') ? n : USAGE_WARN_AT;
  }

  private get dangerAt(): number {
    const n = Number(this.getAttribute('danger-at'));
    return Number.isFinite(n) && this.hasAttribute('danger-at') ? n : USAGE_DANGER_AT;
  }

  /**
   * The intent of one limit, by the same rule its own bar will apply. A limit with no usable
   * ceiling has no proportion and so carries no pressure — it is drawn, but it cannot be
   * "nearly out" of an allowance it does not have.
   */
  private intentOf(limit: CaosLimit): CaosUsageIntent {
    if (!Number.isFinite(limit.maximum) || limit.maximum <= 0) return 'accent';
    const percent = Math.max(0, Math.min(100, (limit.used / limit.maximum) * 100));
    return usageIntent(percent, this.warnAt, this.dangerAt);
  }

  /** How many limits sit at each pressure level — the counts the summary reports. */
  get pressure(): { danger: number; warning: number; total: number } {
    let danger = 0;
    let warning = 0;
    for (const limit of this.data) {
      const intent = this.intentOf(limit);
      if (intent === 'danger') danger += 1;
      else if (intent === 'warning') warning += 1;
    }
    return { danger, warning, total: this.data.length };
  }

  /** The groups, in the order they first appear in `limits` — never sorted. */
  private grouped(): Array<{ name: string | undefined; limits: CaosLimit[] }> {
    const order: Array<string | undefined> = [];
    const byGroup = new Map<string | undefined, CaosLimit[]>();
    for (const limit of this.data) {
      const key = limit.group && limit.group.trim() !== '' ? limit.group : undefined;
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key)!.push(limit);
    }
    return order.map((name) => ({ name, limits: byGroup.get(name)! }));
  }

  private render(): void {
    const { danger, warning, total } = this.pressure;

    if (total === 0) {
      this.summaryEl.textContent = '';
      this.summaryEl.removeAttribute('data-pressure');
      this.bodyEl.replaceChildren(this.emptyNode());
      return;
    }

    this.summaryEl.replaceChildren(...this.summaryNodes(danger, warning, total));
    this.summaryEl.setAttribute('data-pressure', danger > 0 ? 'danger' : warning > 0 ? 'warning' : 'none');

    const groups = this.grouped().map(({ name, limits }) => {
      const groupEl = document.createElement('section');
      groupEl.className = 'group';
      groupEl.setAttribute('part', 'group');

      if (name !== undefined) {
        const nameEl = document.createElement('h3');
        nameEl.className = 'group-name';
        nameEl.setAttribute('part', 'group-name');
        nameEl.textContent = name;
        groupEl.append(nameEl);
        // The group heading names the list beneath it for a screen reader too, not only visually.
        groupEl.setAttribute('aria-label', name);
      }

      const listEl = document.createElement('div');
      listEl.className = 'limits';
      for (const limit of limits) listEl.append(this.barFor(limit));
      groupEl.append(listEl);
      return groupEl;
    });

    this.bodyEl.replaceChildren(...groups);
  }

  private barFor(limit: CaosLimit): HTMLElement {
    const bar = document.createElement('caos-usage-bar');
    bar.setAttribute('part', 'limit');
    bar.setAttribute('label', limit.label);
    bar.setAttribute('used', String(limit.used));
    if (Number.isFinite(limit.maximum)) bar.setAttribute('maximum', String(limit.maximum));
    if (limit.unit) bar.setAttribute('unit', limit.unit);
    // The panel's thresholds are handed down, so a bar and the summary above it cannot answer
    // the same question differently.
    bar.setAttribute('warn-at', String(this.warnAt));
    bar.setAttribute('danger-at', String(this.dangerAt));
    return bar;
  }

  private summaryNodes(danger: number, warning: number, total: number): Node[] {
    const nodes: Node[] = [];
    const strong = (text: string): HTMLElement => {
      const el = document.createElement('strong');
      el.textContent = text;
      return el;
    };
    if (danger === 0 && warning === 0) {
      nodes.push(document.createTextNode(`${total} ${plural(total, 'limit')}, `), strong('all healthy'));
      return nodes;
    }
    const parts: string[] = [];
    if (danger > 0) parts.push(`${danger} at the limit`);
    if (warning > 0) parts.push(`${warning} approaching`);
    nodes.push(
      document.createTextNode(`${total} ${plural(total, 'limit')} — `),
      strong(parts.join(', ')),
    );
    return nodes;
  }

  private emptyNode(): HTMLElement {
    const el = document.createElement('p');
    el.className = 'empty';
    el.textContent = this.getAttribute('empty-text') ?? 'No limits reported.';
    return el;
  }
}

function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

define('caos-limits-panel', CaosLimitsPanel);

declare global {
  interface HTMLElementTagNameMap {
    'caos-limits-panel': CaosLimitsPanel;
  }
}
